import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import '../../components/HostedCheckout/HostedCheckout.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { ANALYTICS_EVENTS, capturePostHogEvent, pushEvent } from '../../utils/analytics';
import {
  buildHostedCheckoutNavigation,
  completeHostedCheckoutReturn,
  HostedCheckoutError,
} from '../../utils/hostedCheckout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
const MAX_PROCESSING_POLLS = 10;

export default function HostedCheckoutReturnPage() {
  const { handleCustomJwtAuth } = useUnifiedAuth();
  return <HostedCheckoutReturn authenticate={handleCustomJwtAuth} />;
}

export function HostedCheckoutReturn({
  authenticate,
}: {
  authenticate: (token: string) => Promise<boolean>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const requestRef = useRef<{
    search: string;
    promise: ReturnType<typeof completeHostedCheckoutReturn>;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    const params = new URLSearchParams(location.search);
    const attemptId = params.get('attempt');
    const returnToken = params.get('state');
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled') === 'true';
    if (!attemptId || !returnToken || (!canceled && !sessionId)) {
      setError('Checkout return link is incomplete.');
      return;
    }

    let active = true;
    let pollTimer: number | undefined;
    const finish = async () => {
      for (let poll = 0; poll < MAX_PROCESSING_POLLS && active; poll += 1) {
        if (requestRef.current?.search !== location.search) {
          requestRef.current = {
            search: location.search,
            promise: completeHostedCheckoutReturn({
              apiUrl: API_URL,
              attemptId,
              returnToken,
              sessionId,
              canceled,
            }),
          };
        }
        const request = requestRef.current;
        let result;
        try {
          result = await request.promise;
        } catch (requestError) {
          if (requestRef.current === request) requestRef.current = null;
          // The return endpoint is idempotent. A paid buyer must not be left on
          // an error page because one Stripe/reconciliation request returned a
          // transient 502; retry the same signed proof within the existing
          // bounded processing loop. Permanent proof, ownership, and payment
          // failures keep their explicit error and are never retried.
          if (
            requestError instanceof HostedCheckoutError &&
            requestError.code === 'HOSTED_CHECKOUT_UNAVAILABLE' &&
            poll < MAX_PROCESSING_POLLS - 1
          ) {
            await new Promise<void>((resolve) => {
              pollTimer = window.setTimeout(resolve, 1000);
            });
            continue;
          }
          throw requestError;
        }
        if (!active) return;
        if (requestRef.current === request) requestRef.current = null;
        if (result.status === 'canceled') {
          capturePostHogEvent('checkout_experience_cancelled', {
            checkout_attempt_id: attemptId,
          });
          // "Have a different promo code? Enter it here" on the Stripe page
          // comes back through this cancel return (Rob, 2026-09-29).
          // Stripe opens that link in a new tab with no cart, so the link
          // carries the purchase and checkout is rebuilt from it.
          const wantsPromoEntry = params.get('promo_entry') === 'true';
          const promoEntryPath = () => {
            const checkoutParams = new URLSearchParams();
            const products = params.getAll('product');
            const planKeys = params.getAll('planKey');
            const quantities = params.getAll('quantity');
            products.forEach((product, index) => {
              checkoutParams.append('product', product);
              checkoutParams.append('planKey', planKeys[index] || 'default');
              checkoutParams.append('quantity', quantities[index] || '1');
            });
            checkoutParams.set('promoEntry', '1');
            return `/checkout?${checkoutParams.toString()}`;
          };
          navigate(
            wantsPromoEntry
              ? promoEntryPath()
              : result.cancelPath || '/pricing?checkoutCanceled=true',
            { replace: true },
          );
          return;
        }
        if (result.status === 'processing') {
          await new Promise<void>((resolve) => {
            pollTimer = window.setTimeout(resolve, 1000);
          });
          continue;
        }
        if (result.token) {
          const authenticated = await authenticate(result.token);
          if (!active) return;
          if (!authenticated)
            throw new Error('Payment verified. Please sign in to open your account.');
        }
        // The custom checkout fires payment_success on its own success; this
        // is the hosted arm's equivalent so Google Ads, Meta, and PostHog see
        // hosted purchases too. Before 2026-09-11 hosted purchases fired nothing.
        if (result.purchase?.transactionId) {
          pushEvent({
            event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
            transaction_id: result.purchase.transactionId,
            currency: result.purchase.currency || 'USD',
            value: typeof result.purchase.value === 'number' ? result.purchase.value : undefined,
            plan_type: (result.purchase.productSlugs || []).join(','),
            source: '/checkout/hosted-return',
            event_origin: 'hosted_checkout',
            checkout_attempt_id: attemptId,
          });
        }
        const destination = buildHostedCheckoutNavigation({
          redirectTo: result.redirectTo,
          returnPath: result.returnPath,
          userEmail: result.userEmail,
          origin: window.location.origin,
        });
        navigate(destination.path, {
          replace: true,
          state: destination.state,
        });
        return;
      }
      if (active)
        throw new Error(
          'Payment is still processing. Check your email or account in a few minutes.',
        );
    };
    void finish().catch((returnError) => {
      if (active)
        setError(
          returnError instanceof Error ? returnError.message : 'Checkout could not be verified.',
        );
    });
    return () => {
      active = false;
      window.clearTimeout(pollTimer);
    };
  }, [authenticate, location.search, navigate]);

  return (
    <main className="checkout-page" aria-live="polite">
      <div className="checkout-loading hosted-checkout-status">
        {error ? (
          <>
            <h1>Payment needs review</h1>
            <p role="alert">{error}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/account-settings/subscriptions')}
            >
              View account
            </button>
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Verifying payment and opening your account…</span>
          </>
        )}
      </div>
    </main>
  );
}
